import { Divider, Stack } from "@mui/material"
import { ReactElement } from "react"

import ExportLimitDeviceCard from "./exportLimitDeviceCard"
import { TAttachment } from "../../../services/middleware/mappers/dnoInterface/dnoApplicationsMapper"
import { GRID_SPACING } from "../../../themes/defaultThemeParams"
import VerticalKeyValueList from "../../dataDisplay/verticalKeyValueList"
import { formatKeys } from "../singleApplicationKeys"

interface IExportLimitDeviceDetails {
    registeredCapacity: string
    exportLimitDeviceClass: string
    exportLimitSLDAttachment: TAttachment
    exportLimitDeviceDescription: string | null
    g100ExportLimitDeviceSysRef: string
}
interface IExpectedDetails {
    isExportLimitDevicePresent: boolean
    exportLimitDeviceDetails: IExportLimitDeviceDetails | null
}
interface ISupplyDetailsProps<T> {
    supplyDetails: T
    applicationId: string
    disabled?: boolean
}

/**
 * Card for displaying details about the supply on a property. Designed for use
 * on the single application summary page. This component has been designed to
 * be schema version agnostic. It assumes there are 2 recognised fields related
 * to export limit schemes and a number of unrecognised fields. This assumption
 * is used to destructure `supplyDetails`.
 * props.
 */
const SupplyDetails = <T extends object>({
    supplyDetails,
    applicationId,
    disabled,
}: ISupplyDetailsProps<T>): ReactElement => {
    const { isExportLimitDevicePresent, exportLimitDeviceDetails, ...details } =
        supplyDetails as IExpectedDetails

    let registeredCapacity
    let type
    let sldAttachment
    let exportLimitDeviceDescription
    let g100DeviceSysRef
    if (!exportLimitDeviceDetails) {
        registeredCapacity = "Failed to load"
        type = "Failed to load"
        exportLimitDeviceDescription = "Failed to load"
        sldAttachment = {
            fileUrl: "Failed to load",
        }
        g100DeviceSysRef = "Failed to load"
    } else {
        registeredCapacity = exportLimitDeviceDetails?.registeredCapacity
        type = exportLimitDeviceDetails?.exportLimitDeviceClass
        sldAttachment = exportLimitDeviceDetails?.exportLimitSLDAttachment
        exportLimitDeviceDescription =
            exportLimitDeviceDetails?.exportLimitDeviceDescription
        g100DeviceSysRef = exportLimitDeviceDetails?.g100ExportLimitDeviceSysRef
    }

    return (
        <Stack spacing={GRID_SPACING - 1} divider={<Divider />}>
            <VerticalKeyValueList
                kwargs={formatKeys(details) as Record<string, string>}
            />
            {isExportLimitDevicePresent && (
                <ExportLimitDeviceCard
                    capacity={registeredCapacity}
                    type={type}
                    sldAttachment={sldAttachment}
                    exportLimitDeviceDescription={exportLimitDeviceDescription}
                    g100DeviceSysRef={g100DeviceSysRef}
                    applicationId={applicationId}
                    disabled={disabled}
                />
            )}
        </Stack>
    )
}

SupplyDetails.defaultProps = {
    disabled: false,
}

export default SupplyDetails
// Other has capacity and comments
// Manufacture has capacity and comments
// G100 has capacity and link
